import { useCallback, useEffect, useState } from 'react';
import { useCreateAndNavigate } from '../../../../hooks/entity/useCreateAndNavigate';
import { putPendingCaptureFile } from '../../../../stores/pendingCaptureFileStore';
import { DropZone } from '../../../upload/intake/DropZone';
import { IMAGE } from '../../../upload/intake/rules';
import { useFileIntake } from '../../../upload/intake/useFileIntake';
import type { CreateBodyProps } from './createBodyProps';
import { destinationParent } from '../destination/createDestination';

/**
 * Photo → text: drop or choose the photo here, and only then is the note
 * made. The picker opens from this click, inside the browser's user
 * activation; opened by the note page after the create round trip, it could
 * be refused. Leaving without a photo makes nothing.
 */
export function PhotoBody({
  destination,
  surface,
  onDone,
  onDestinationProblem,
  onBusyChange,
}: CreateBodyProps) {
  const commit = useCreateAndNavigate(surface, onDestinationProblem);
  const [isCreating, setIsCreating] = useState(false);
  useEffect(() => onBusyChange(isCreating), [isCreating, onBusyChange]);

  const makeNote = useCallback(
    async (photo: File) => {
      setIsCreating(true);
      try {
        const ok = await commit(destinationParent(destination), 'note', 'image_to_text', (id) =>
          putPendingCaptureFile(id, photo),
        );
        if (ok) onDone();
      } finally {
        setIsCreating(false);
      }
    },
    [commit, destination, onDone],
  );

  const intake = useFileIntake({
    rule: IMAGE,
    surface: 'create_photo',
    maxFiles: 1,
    readOnlyReason: isCreating ? 'Wait for this one to finish first.' : undefined,
    onFiles: ([photo]) => void makeNote(photo),
  });

  return (
    <DropZone
      intake={intake}
      icon="add_photo_alternate"
      title="Drop a photo, or click to choose one"
      touchTitle="Add a photo"
      next="A page of a lab notebook, a whiteboard, a printed label. Bower writes out the text in a new note."
    />
  );
}
